import { UnknownAction } from '@reduxjs/toolkit';
import { lastValueFrom } from 'rxjs';
import Router from 'next/router';

import {
  connectFromOtherDomain, connectFromOtherDomainSuccess,
  createCustomer, createCustomerError, createCustomerSuccess,
  getOrders, getOrdersError, getOrdersSuccess,
  setActAsCustomer, setActAsCustomerSuccess,
  updateFreeStorageUser
} from '../slices/admin.slice';

import { hidePageProgress, setHeaderColor, toggleConfirmationModal } from '../slices/gui.slice';
import { getUser } from '../slices/user.slice';
import { getCart } from '../slices/cart.slice';
import { getBox } from '../slices/box.slice';
import { getUserNotebook } from '../slices/notebook.slice';
import { getWishList } from '../slices/wishList.slice';
import { OrderDataModel, UserModel } from '../../models';
import { addApiErrorToast, addToast } from '../slices/toast.slice';

import { NS } from '@/constants';
import { isFeatureEnabled } from '@/utils/millesimaUtils';
import { AppListenerEntry } from '@/redux/create-store';
import { ACCOUNT_TABS } from '@/constants/account';
import { FEATURES } from '@/constants/features';
import { PAGES } from '@/constants/pages';
import { STORE_ID_DEFAULT } from '@/constants/stores';

const redirectionTo = (url: string): void => {
  window.location.href = url;
};

export const epicActAsCustomer: AppListenerEntry = {
  actionCreator: setActAsCustomer,
  effect: async ({ payload }: ReturnType<typeof setActAsCustomer>, { getState, dispatch, extra: { authService } }) => {
    const { customer, changeStore, finalUrl, redirectHome, t } = payload;

    if (changeStore && finalUrl) {
      redirectionTo(`${finalUrl}?token=${authService.getAuthToken()}&actAsCustomer=${customer._id}`);
      return [];
    }

    const { root: { storeConf }, auth } = getState();

    if (customer) {
      authService.setActAsCustomer({ ...customer });
    } else {
      authService.setActAsCustomer(undefined);
    }

    const actions: UnknownAction[] = [
      setActAsCustomerSuccess(),
      setHeaderColor({ actAsCustomer: customer })
    ];

    if (auth.loggedUser) {
      actions.push(getUser(), getCart(), getBox());

      if (customer) {
        actions.push(getUserNotebook({ pickId: customer.pickId }));
      }

      if (isFeatureEnabled(storeConf, FEATURES.MODULE_WISHLIST)) {
        actions.push(getWishList());
      }
    }

    if (!customer?.email && t) {
      actions.push(toggleConfirmationModal({
        opened: true,
        title: t('users.modal.addEmail', { ns: NS.ADMIN }),
        customContent: t('users.modal.contentAddEmail', { ns: NS.ADMIN }),
        confirm: {
          label: t('btn.continue'),
          action: () => Router.push(`${PAGES.ACCOUNT}/${ACCOUNT_TABS.PROFILE}`).finally(() => dispatch(hidePageProgress()))
        },
        cancel: {
          label: t('btn.cancel')
        }
      }));
    }

    if (redirectHome) {
      await Router.push('/', undefined);
    }

    actions.forEach((action) => dispatch(action));
  }
};

export const epicConnectFromOtherDomain: AppListenerEntry = {
  actionCreator: connectFromOtherDomain,
  effect: async ({ payload }: ReturnType<typeof connectFromOtherDomain>, { dispatch, extra: { authService } }) => {
    const { customerId, token, storeId } = payload;
    try {
      await lastValueFrom(authService.connectFromOtherDomain(storeId, { customer: customerId, token }));
      dispatch(connectFromOtherDomainSuccess({ customerId }));
    } catch (error) {
      Router.push('/', undefined);
      dispatch(addApiErrorToast({ error }));
    }
  }
};

export const epicConnectFromOtherDomainSuccess: AppListenerEntry = {
  actionCreator: connectFromOtherDomainSuccess,
  effect: async ({ payload }: ReturnType<typeof connectFromOtherDomainSuccess>, { dispatch, extra: { userService } }) => {
    const { customerId } = payload;
    try {
      const user = await lastValueFrom(userService.getUserById(customerId)) as UserModel;
      dispatch(setActAsCustomer({ customer: user, redirectHome: true }));
    } catch (error) {
      Router.push('/', undefined);
      return dispatch(addApiErrorToast({ error }));
    }
  }
};

export const epicCreateCustomer: AppListenerEntry = {
  actionCreator: createCustomer,
  effect: async ({ payload }: ReturnType<typeof createCustomer>, { getState, dispatch, extra: { adminService } }) => {
    const { root } = getState();
    const { customer, creator } = payload;
    const { storeId = STORE_ID_DEFAULT } = root.storeConf;
    try {
      const createdCustomer = await lastValueFrom(adminService.createCustomer(storeId, customer, creator)) as UserModel;
      dispatch(createCustomerSuccess({ createdCustomer }));
    } catch (error) {
      dispatch(createCustomerError({ error }));
    }
  }
};

export const epicGetOrders: AppListenerEntry = {
  actionCreator: getOrders,
  effect: async ({ payload }: ReturnType<typeof getOrders>, { dispatch, extra: { orderService } }) => {
    const { page, sort, filters } = payload;
    try {
      const ordersData = await lastValueFrom(orderService.getOrders(page, sort, filters)) as OrderDataModel;
      dispatch(getOrdersSuccess({ ordersData }));
    } catch (error) {
      dispatch(getOrdersError({ error }));
    }
  }
};

export const epicUpdateFreeStorageUser: AppListenerEntry = {
  actionCreator: updateFreeStorageUser,
  effect: async ({ payload }: ReturnType<typeof updateFreeStorageUser>, { dispatch, extra: { adminService } }) => {
    const { userId, t } = payload;
    try {
      await lastValueFrom(adminService.updateFreeStorage(userId));
      dispatch(addToast({ msg: t('admin.freeStorageUpdated'), err: false }));
      dispatch(getUser());
    } catch (error) {
      dispatch(addToast({ msg: t('errors.genericErrorMessage') }));
    }
  }
};
